import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function BasicOptionsPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-4">Basic Content Options</h1>
        <p className="text-lg text-gray-700 mb-6">
          The basic options are the foundation of your content creation process. They help you define what your content is about and set the parameters for the AI to work with.
        </p>
      </div>

      <div className="prose max-w-none">
        <h2>Essential Settings</h2>
        
        <h3>Content Type</h3>
        <p>
          Selecting the right content type helps the AI understand the structure and purpose of your blog post. 
          Each content type is optimized for different goals.
        </p>
        
        <div className="not-prose my-6 bg-white border rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h4 className="font-medium">Available Content Types</h4>
          </div>
          <div className="p-4">
            <ul className="space-y-4">
              <li className="p-3 border rounded-md">
                <div className="font-semibold">Listicle</div>
                <p className="text-sm text-gray-600 mt-1">
                  A strategically organized, numbered article that transforms complex topics into engaging, scannable segments.
                </p>
              </li>
              <li className="p-3 border rounded-md">
                <div className="font-semibold">How-To Guide</div>
                <p className="text-sm text-gray-600 mt-1">
                  An authoritative, problem-solving resource that transforms complex processes into accessible, sequential instructions.
                </p>
              </li>
              <li className="p-3 border rounded-md">
                <div className="font-semibold">Comparison</div>
                <p className="text-sm text-gray-600 mt-1">
                  A decision-enabling analysis that systematically evaluates competing options against user-centric criteria.
                </p>
              </li>
            </ul>
          </div>
        </div>

        <h3>Blog Title</h3>
        <p>
          Your blog title is crucial for both SEO and reader engagement. A good title should:
        </p>
        <ul>
          <li>Include your primary keyword</li>
          <li>Be clear and specific about the content</li>
          <li>Create curiosity or promise value</li>
          <li>Stay between 50-60 characters for optimal display in search results</li>
        </ul>
        
        <p className="bg-yellow-50 border-l-4 border-yellow-400 p-4 not-prose">
          <strong>Example:</strong> Instead of "Marketing Tips," use "7 Data-Driven Marketing Tips That Doubled Our Conversions in 2025"
        </p>

        <h3>Keywords</h3>
        <p>
          Keywords tell the AI which terms to focus on for SEO optimization. Enter your primary keyword first, 
          followed by secondary keywords separated by commas.
        </p>
        
        <div className="not-prose bg-gray-50 p-4 rounded-md my-4">
          <p className="text-sm">
            <strong>Good example:</strong> content marketing, lead generation, inbound strategy, B2B marketing
          </p>
          <p className="text-sm mt-2">
            <strong>Poor example:</strong> marketing, content, strategy, business (too generic)
          </p>
        </div>

        <h3>Industry</h3>
        <p>
          Specifying your industry helps the AI generate content with relevant examples and terminology. 
          Be as specific as possible for more tailored content.
        </p>
        
        <h3>Word Count</h3>
        <p>
          The word count setting determines the approximate length of your blog post. For SEO purposes:
        </p>
        <ul>
          <li>Short-form content: 500-1,000 words</li>
          <li>Medium-form content: 1,000-2,000 words</li>
          <li>Long-form content: 2,000+ words</li>
        </ul>
        
        <p>
          Longer content typically ranks better for competitive keywords, but quality always trumps quantity.
        </p>

        <div className="not-prose bg-blue-50 border border-blue-100 rounded-lg p-6 my-6">
          <h4 className="text-lg font-medium text-blue-800 mb-2">Word Count by Subscription Tier</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-medium">Free Tier:</span>
              <span>Up to 2,000 words per post</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium">Pro Tier:</span>
              <span>Up to 4,000 words per post</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium">Enterprise Tier:</span>
              <span>Up to 4,000 words per post</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium">Enterprise VIP Tier:</span>
              <span>Up to 8,000 words per post</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border rounded-lg p-6 bg-gray-50">
        <h3 className="text-lg font-semibold mb-4">Toggles Explained</h3>
        
        <div className="space-y-4">
          <div className="bg-white p-4 rounded border">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-10 h-5 bg-gray-200 rounded-full mr-3"></div>
                <span className="font-medium">Introduction</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              When enabled, the AI will include a comprehensive introduction that sets up the topic and engages readers.
            </p>
          </div>
          
          <div className="bg-white p-4 rounded border">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-10 h-5 bg-blue-600 rounded-full mr-3 relative">
                  <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                </div>
                <span className="font-medium">Longer Paragraphs</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              <strong className="text-blue-700">Highly recommended</strong>. Creates more detailed, in-depth paragraphs that provide comprehensive coverage of each point.
            </p>
          </div>
          
          <div className="bg-white p-4 rounded border">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-10 h-5 bg-gray-200 rounded-full mr-3"></div>
                <span className="font-medium">Conclusion</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Adds a summary conclusion that ties together the main points and reinforces key insights.
            </p>
          </div>
          
          <div className="bg-white p-4 rounded border">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-10 h-5 bg-blue-600 rounded-full mr-3 relative">
                  <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                </div>
                <span className="font-medium">Key Takeaways</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Includes a final section with key points summarized for quick reference, which improves reader retention.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button variant="outline" asChild>
          <Link to="/docs/writer">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back: Content Writer
          </Link>
        </Button>
        <Button asChild>
          <Link to="/docs/writer/marketing-options">
            Next: Marketing Options
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}